// MAO KINGDOM — entry point. Imports scene, game state, UI; starts render loop.
//
// Architecture: modules talk through src/ui/eventBus.js. Direct cross-module
// calls are avoided so each subsystem can be swapped without rewiring the others.

import { supabase as supabaseClient } from './game/supabase.js'
import { createScene } from './scene/sceneCore.js'
import { createDayNight } from './scene/dayNight.js'
import { initNightLights, initFireflies, setFireflyFocus } from './scene/nightLights.js'
import { createPostFX } from './scene/postfx.js'
import { createPaddy } from './scene/paddy.js'
import { loadSynty, setSyntyNight } from './scene/synty.js'
import { loadSyntyChars, tickSyntyChars } from './scene/syntyChars.js'
import { createCat } from './scene/cat.js'
import { createPeasants, tickPeasants } from './scene/peasants.js'
import { createSparkles } from './scene/sparkles.js'
import { createRiceField } from './scene/riceField.js'
import * as loopFeedback from './game/loopFeedback.js'
import * as glint from './game/glint.js'
import { createGlintFx } from './scene/glintFx.js'
import { createCrow } from './scene/crow.js'
import { mountFieldClicks } from './scene/fieldClicks.js'
import { createHospitalBadge } from './scene/hospitalBadge.js'
import { directCitizen, getPeasantObjects } from './scene/peasants.js'
import { countByType as plotsOfType } from './game/plotState.js'
import { PlotTypes as PlotKinds } from './config/balance.js'
import { createWildGoons, tickWildGoons, clearWildGoons } from './scene/wildGoons.js'
import { createWives, tickWives } from './scene/wives.js'
import { attachHeroController, tickHero, tickCamera, hackToHome, hasWalkRules } from './scene/heroController.js'
import { createBuildings } from './scene/buildings.js'
import { createPositiveTool } from './scene/positiveTool.js'
import { createBuildingLabels } from './scene/buildingLabels.js'
import { createPlacement } from './scene/placement.js'
import { createAdventureScene, tickAdventureScene } from './scene/adventureScene.js'
import { createCapitalScene, tickCapitalScene } from './scene/capitalScene.js'
import { createLandScene, tickLandScene } from './scene/landScene.js'
import { mountHud, pushLog } from './ui/hud.js'
import { formatCoins } from './ui/format.js'
import { showPurchaseCelebration } from './ui/purchaseCelebration.js'
import { mountStatsPanel } from './ui/statsPanel.js'
import { mountGoonsPanel } from './ui/goonsPanel.js'
import { mountBuildPanel } from './ui/buildPanel.js'
import { mountFeedPanel } from './ui/feedPanel.js'
import { mountTaxPanel } from './ui/taxPanel.js'
import { mountKingdomTabs } from './ui/kingdomTabs.js'
import { mountKingdomViewPanel } from './ui/kingdomViewPanel.js'
import { mountPopulationPanel } from './ui/populationPanel.js'
import { mountCareerPanel } from './ui/careerPanel.js'
import { mountCloudPanel } from './ui/cloudPanel.js'
import { mountSettingsPanel } from './ui/settingsPanel.js'
import { initAccent } from './game/accent.js'
import { mountFeedNudges } from './ui/feedNudges.js'
import { mountBannersPanel } from './ui/bannersPanel.js'
import { mountMessageRail } from './ui/messageRail.js'
import { mountOnboardingPanel } from './ui/onboardingPanel.js'
import { mountIdlePanel } from './ui/idlePanel.js'
import { mountRegistryPanel } from './ui/registryPanel.js'
import { mountRegistryPage } from './ui/pages/registryPage.js'
import { refresh as refreshInvites, attention as inviteAttention, getInvites, setPollMode as setInvitePollMode, getPollMode as getInvitePollMode } from './game/inviteManager.js' // (listens for sign-in from the start)
import { mountInvitePopup } from './ui/invitePopup.js'
import './game/coopManager.js' // (host tap, credits, the guest's visit)
import { mountCoopPanel } from './ui/coopPanel.js'
import './game/genderManager.js' // (asks existing Maos for a gender once)
import { mountGenderPrompt } from './ui/genderPrompt.js'
import { mountProposalPopup } from './ui/proposalPopup.js'
import { mountHouseholdReport } from './ui/householdReport.js'
import { mountAdventurePanel } from './ui/adventurePanel.js'
import { mountUiMinimap } from './ui/uiMinimap.js'
import { mountMusicPanel } from './ui/musicPanel.js'
import * as music from './game/musicManager.js'
import { mountViewRotateControls } from './ui/viewRotateControls.js'
import { mountCapitalButton } from './ui/capitalButton.js'
import { mountCapitalRailLock } from './ui/capitalRailLock.js'
import { mountCapitalTripUI } from './ui/capitalTripUI.js'
import { mountCapitalDealsUI } from './ui/capitalDealsUI.js'
import { mountLoginModal } from './ui/loginModal.js'
import { mountResetPasswordModal } from './ui/resetPasswordModal.js'
import { mountStudyModal } from './ui/studyModal.js'
import { mountQuizModal } from './ui/quizModal.js'
import { mountGamepadCursor } from './ui/gamepadCursor.js'
import { mountControllerSetup } from './ui/controllerSetup.js'
import { mountAdminPanel } from './ui/adminPanel.js'
import { mountRepairPanel } from './ui/repairPanel.js'
import * as siteRouter from './ui/siteRouter.js'
import { mountSiteHeader } from './ui/siteHeader.js'
import { mountHomePage } from './ui/pages/homePage.js'
import { mountStubPage } from './ui/pages/stubPage.js'
import { mountShopPage } from './ui/pages/shopPage.js'
import { mountGameGuide } from './ui/pages/gameGuide.js'
import { mountOverviewPage } from './ui/pages/overviewPage.js'
import { on, Events } from './ui/eventBus.js'
import { WildGoonConfig, FriendlyFirePenalty, GoonConfig } from './config/balance.js'
import * as season from './game/seasonManager.js'
import * as goons from './game/goonManager.js'
import * as buildings from './game/buildingManager.js'
import * as feed from './game/feedManager.js'
import * as tax from './game/taxManager.js'
import * as rest from './game/restManager.js'
import * as population from './game/populationManager.js'
import * as profile from './game/profileManager.js'
import * as membership from './game/membershipManager.js'
import * as marriage from './game/marriageManager.js'
import * as achievements from './game/achievementManager.js'
import * as adventures from './game/adventureManager.js'
import * as damage from './game/damageManager.js'
import * as election from './game/electionManager.js'
import * as account from './game/accountManager.js'
import * as idle from './game/idleManager.js'
import * as raidDirector from './game/raidDirector.js'
import { mountThreatChip } from './ui/threatChip.js'
import * as hospital from './game/hospital.js'
import { mountHospitalChip } from './ui/hospitalChip.js'
import { createCemetery } from './scene/cemetery.js'
import { mountPlacementConfirmUI } from './ui/placementConfirmUI.js'
import { mountSessionBlocked } from './ui/sessionBlocked.js'
import * as events from './game/eventManager.js'
import * as narrative from './game/narrativeManager.js'
import * as achievementToast from './ui/achievementToast.js'
import { playClick } from './game/audio.js'
import * as Assets from './assets/AssetRegistry.js'
import { getProfile, state as gameState, persist as persistState, active as activeKingdom } from './game/state.js'
import { tileToWorld } from './game/plotState.js'

async function boot() {
  // Apply the player's chosen UI accent before anything paints.
  initAccent()

  // Set the series based on the player's profile (defaults to Series 3 on
  // fresh saves) and preload its .glb assets. Missing files fall back to the
  // programmatic placeholders inside each scene module — boot proceeds either
  // way.
  Assets.setSeries(getProfile().seriesId ?? Assets.DEFAULT_SERIES)
  // The Synty low-poly kit replaces the 6.7 MB Tiles.glb; the old tiles load only if the kit can't.
  const [syntyOk] = await Promise.all([
    loadSynty().then(() => true, (err) => { console.warn('[synty] hex kit unavailable, using classic tiles:', err.message || err); return false }),
    Assets.preload(undefined, { skipMasters: ['Tiles'] }),
    loadSyntyChars().catch((err) => console.warn('[synty] characters unavailable, using cats:', err.message || err)),
  ])
  if (!syntyOk) await Assets.preloadMaster('Tiles')

  // ── Site shell first — mount router + header + non-Play pages ────────────
  siteRouter.init()
  mountSiteHeader({ root: document.querySelector('[data-site-header]') })
  mountLoginModal() // site-level Log in overlay (header "Log in")
  mountResetPasswordModal() // "choose a new password" after the reset-email link
  mountStudyModal() // CR-6 study-guide reading page (Academy → study guide)
  mountQuizModal()  // CR-6 degree-quiz overlay (Academy → take quiz)
  mountGamepadCursor() // controller 1B: virtual cursor for menu navigation
  mountControllerSetup() // gamepad remap wizard (auto-opens on first connect)
  mountHomePage({ root: document.querySelector('[data-page-view="home"]') })
  mountShopPage({ root: document.querySelector('[data-page-view="shop"]') })
  mountStubPage({
    root: document.querySelector('[data-page-view="vland"]'),
    title: 'Virtual Land',
    subtitle: 'Own a parcel inside your kingdom\'s territory.',
    sections: [
      { title: 'ABOUT VLAND', desc: 'What virtual land means in the Mao Kingdom — its boundaries, its taxes, its inheritance.' },
      { title: 'PARCEL MAP', desc: 'Browse available parcels across Wei, Han, and Wu.' },
    ],
  })
  mountGameGuide({ root: document.querySelector('[data-page-view="guide"]') })
  mountRegistryPage({ root: document.querySelector('[data-page-view="registry"]') })
  mountInvitePopup() // co-op invitation pop-up (any page)
  mountProposalPopup() // marriage proposals (like invitations)
  mountHouseholdReport() // a husband's report on his wives, once per sign-in
  mountGenderPrompt() // one-time "male or female?" for a Mao adopted before genders existed
  mountCoopPanel()   // co-op visit cards: guests in your kingdom / your visit
  on(Events.CoopNotice, ({ text, feed }) => { if (feed) pushLog(text) }) // (the scribe's log carries what really happened)
  mountOverviewPage({ root: document.querySelector('[data-page-view="overview"]') })

  // ── Game shell ───────────────────────────────────────────────────────────
  const canvas = document.getElementById('scene')
  const { renderer, scene, camera, ambient, sun } = createScene({ canvas })
  initNightLights(scene) // night glows: the buildings' lantern props light themselves (see synty.js) + the campfires
  initFireflies()        // a few small buzzing fireflies that stay near the player, lit only at night
  // Day/night environment driven by the player's local clock (sky + lights + glows).
  const dayNight = createDayNight({ scene, ambient, sun })
  // Bloom (dreamy glow bleed) — only rendered after dark; strength tracks the
  // night factor so daytime keeps the plain transparent render + CSS sky.
  const post = createPostFX({ renderer, scene, camera, canvas })
  if (import.meta.env?.DEV) window.__dayNight = dayNight // dev: __dayNight.setHour(22)

  const fx = createSparkles({ scene, camera })   // one pool of glowing motes: click sparks, tending puffs, the harvest burst
  const rice = createRiceField({ scene })         // every paddy's crop rows in one instanced mesh, growing with the season's clicks
  const paddy = createPaddy({ scene, camera, canvas, rice })
  const hero = createCat({ scene })
  createPeasants({ scene, hero, fx, rice })
  loopFeedback.init({ rice, fx }) // the 10-second loop's sounds + flourishes (game/loopFeedback.js)
  // the field's creatures: the golden glint (an opportunity), the crow (a threat) — only on the Play page, with paddies, no raid on
  const onField = () => siteRouter.getActivePage() === 'play' && plotsOfType(PlotKinds.FARM) > 0 && !hasWalkRules() && !adventures.isAdventuring()
  glint.init({ canSpawn: onField })
  createGlintFx({ rice, fx })
  const crow = createCrow({ scene, rice, fx, canSpawn: () => onField() && !idle.isRaidActive() && (season.getSeasonClicks()[activeKingdom().season] ?? 0) > 0 })
  const hospitalBadge = createHospitalBadge({ scene }) // the 🏥 sign over the Hospital: click to visit
  mountFieldClicks({ canvas, camera, targets: [
    { pick: crow.pick, hit: () => crow.shoo() },                                                 // the crow, before anything
    { pick: hospitalBadge.pick, hit: () => hospitalBadge.open() },                              // the Hospital's sign
    { pick: () => getPeasantObjects().filter((m) => m.visible), hit: (m) => directCitizen(m) },  // a citizen: send it to the next row
  ] })
  // THE FIELD IS THE PROGRESS BAR: each of the player's clicks sends a spark down the row it fills (round-robin over the paddies);
  // the harvest payout bursts grain over every paddy.
  on(Events.Click, () => { const row = rice.sparkRow(fx.inView); if (row) { fx.spark(row); rice.markHot(row.key, row.ri, 1) } })
  on(Events.Harvest, ({ harvestable }) => { if (harvestable > 0) for (const c of rice.paddyCentres()) { fx.burst(c.x, 0.32, c.z, 18); fx.burst(c.x + 0.5, 0.32, c.z - 0.4, 8); fx.burst(c.x - 0.5, 0.32, c.z + 0.4, 8) } })
  if (import.meta.env?.DEV) { window.__fx = fx; window.__gl = () => ({ ...renderer.info.render, programs: renderer.info.programs?.length }) } // dev/test: effect stats, draw calls
  createCemetery({ scene }) // a headstone on the fringe for every citizen lost
  createBuildings({ scene })
  createBuildingLabels({ camera, canvas }) // hover labels on placed buildings
  createPositiveTool({ camera, canvas })   // key 2 — hover a citizen to school/bless/heal
  createWildGoons({ scene, hero })
  createWives({ scene }) // a geisha for every active marriage
  // Game start: the cat appears in front of the house (not inside it) with a golden starburst. Runs once the
  // home diorama's collision is armed (paddy seats it ~1.8 s after boot) and the Play page is on screen.
  {
    let spawned = false
    const spawnCat = () => { if (!spawned && document.body.dataset.activePage === 'play') { spawned = true; hackToHome() } }
    on(Events.PageChange, ({ page }) => { if (page === 'play') setTimeout(spawnCat, 2200) })
    setTimeout(spawnCat, 2600)
  }
  // Hero controller needs the paddy tiles for click-to-move raycasting; mount
  // after createPaddy + createCat so the refs are populated.
  attachHeroController(hero, camera, canvas, paddy.tiles)
  // Adventure biome scene-swap — listens for AdventureStart/End on the bus
  // and hides the home objects in favor of a procedural biome group.
  createAdventureScene({ scene, hero })
  createLandScene({ scene, hero, camera, ambient, sun, renderer }) // the other adventure lands (Bamboo Badlands …): same trip flow as the Capital
  createCapitalScene({ scene, hero }) // the Capital — a separate walkable city (entered from the ADVENTURE panel)

  const hudRoot = document.getElementById('hud')
  mountHud({ root: hudRoot })
  mountStatsPanel({ root: hudRoot.querySelector('[data-stats]') })
  mountGoonsPanel({ root: hudRoot.querySelector('[data-goons]') })
  mountBuildPanel({
    root: hudRoot.querySelector('[data-build-panel]'),
    toggleBtn: hudRoot.querySelector('[data-rail-build]'),
  })
  // FEED + TAX moved off the rail into the CITIZENS panel (they open from there).
  mountFeedPanel({ root: hudRoot.querySelector('[data-feed-panel]'), toggleBtn: null })
  mountTaxPanel({ root: hudRoot.querySelector('[data-tax-panel]'), toggleBtn: null })
  mountKingdomTabs({ root: hudRoot.querySelector('[data-kingdom-tabs]') })
  mountKingdomViewPanel({ root: hudRoot.querySelector('[data-kview-panel]') })
  mountPopulationPanel({
    root: hudRoot.querySelector('[data-pop-panel]'),
    toggleBtn: hudRoot.querySelector('[data-rail-pop]'),
  })
  mountCareerPanel({
    root: hudRoot.querySelector('[data-career-panel]'),
    toggleBtn: hudRoot.querySelector('[data-rail-career]'),
  })
  // CLOUD moved off the rail → opens from the Registry header cloud icon.
  mountCloudPanel({ root: hudRoot.querySelector('[data-cloud-panel]'), toggleBtn: null })
  mountRegistryPanel({
    root: hudRoot.querySelector('[data-registry-panel]'),
    // No rail button — the registry opens from the profile avatar (RegistryOpen).
    // The unread-messages dot rides on that avatar.
    dotHost: hudRoot.querySelector('[data-profile]'),
  })
  mountAdventurePanel({
    root: hudRoot.querySelector('[data-adventure-panel]'),
    toggleBtn: hudRoot.querySelector('[data-rail-adventure]'),
  })
  mountSettingsPanel({ root: hudRoot.querySelector('[data-settings-panel]') })
  mountFeedNudges() // periodic 'claim land / grow rice' tips in the message feed
  // SUPERADMIN overlay — lives on document.body (above everything), opened
  // via Events.AdminOpen from the admin-only button in the CLOUD panel.
  const adminRoot = document.createElement('div')
  document.body.appendChild(adminRoot)
  mountAdminPanel({ root: adminRoot })
  mountRepairPanel({ scene })
  mountMessageRail() // every game message: bottom-centre, one at a time (ui/messageRail.js)
  mountBannersPanel()
  mountOnboardingPanel({ root: hudRoot.querySelector('[data-onboard-panel]') })
  mountIdlePanel()
  // (Old click-a-wild-tile "CLAIM LAND" panel retired — land is bought via
  //  BUILD → "Buy Land Plot" claim mode, see buildPanel/placement.)
  // UI minimap — mounted last so every toggleable panel already exists.
  mountUiMinimap()
  music.initMusic({ isPlayPage: () => siteRouter.getActivePage() === 'play' }) // the soundtracks (Relax / Juice, follows the clicker)
  mountMusicPanel()  // …and its panel, just above the minimap
  mountViewRotateControls()
  mountCapitalButton() // RETURN HOME (shown only while away in the Capital), above the rotate arrows
  mountCapitalRailLock() // BUILD / CITIZENS / CAREER / ADVENTURE are off while you're away in the Capital
  mountCapitalDealsUI() // 'F — do this here' chip at Capital landmarks
  mountThreatChip()     // quiet 'Threat: <level>' readout
  mountHospitalChip()   // 🏥 N in hospital — click to heal (×10 the FEED rate)
  mountPlacementConfirmUI() // rotate / confirm / reposition / cancel chip for placed buildings
  mountSessionBlocked()     // 'Play here instead' screen when the game is active in another window / device
  mountCapitalTripUI() // trip ledger, alerts from home, welcome-home tally
  createPlacement({
    scene,
    camera,
    tiles: paddy.tiles,
    canvas,
    banner: hudRoot.querySelector('[data-placement-banner]'),
  })

  // Paw clicks are gated while the cat is on an adventure. We can't help the
  // field — the cat's away. Show a one-shot hint on the first attempt per
  // adventure so the player understands the lockout.
  let adventureHintShown = false
  on(Events.AdventureStart, () => { adventureHintShown = false })
  on('paw:click', () => {
    if (adventures.isAdventuring()) {
      if (!adventureHintShown) {
        adventureHintShown = true
        const def = adventures.currentDef()
        pushLog(`Your cat is on the ${def?.biome ?? 'road'}. Tend the field on its return.`)
      }
      return
    }
    season.registerClick()
  })

  // Temple conversion log feedback + wild-goon cleanup on kingdom switch.
  on(Events.WildGoonConverted, () => {
    pushLog('A wild goon entered your Temple. Now a loyal Peasant Cat in your service.')
  })
  // CR-3: friendly fire — Press-1 shot that hit your OWN citizen instead of a
  // goon. The shot already removed the citizen (death path); here we apply the
  // coin penalty (−1,000 peasant / −5,000 scribe).
  on(Events.FriendlyFire, (f) => {
    if (!f || f.type === 'hero' || !f.goonId) return
    const penalty = FriendlyFirePenalty[f.goonId] ?? 0
    if (penalty <= 0) return
    season.fineWealth(penalty)
    pushLog(`💥 You shot your own ${GoonConfig[f.goonId]?.name ?? 'citizen'} — −${penalty.toLocaleString()}m and one fewer cat in your service.`)
  })
  on(Events.WildGoonKilled, () => {
    pushLog('A wild goon struck down one of your cats. Build a Temple to catch them.')
  })
  // Citizens drove off a goon but got hurt — throttle so a raid doesn't spam.
  let lastInjuryMsg = 0
  on(Events.CitizenInjured, () => {
    const now = performance.now()
    if (now - lastInjuryMsg < 6000) return
    lastInjuryMsg = now
    pushLog('🩹 Your citizens destroyed a goon but were injured — Feed them or send them to a Hospital to recover.')
  })
  // School graduation — throttle the feed so a cluster of grads doesn't spam.
  let lastEduMsg = 0
  on(Events.CitizenEducated, ({ count, payout }) => {
    const now = performance.now()
    if (now - lastEduMsg < 6000) return
    lastEduMsg = now
    const bonus = payout > 0 ? ` +${formatCoins(payout)} graduation bonus.` : ''
    pushLog(`🎓 A citizen graduated from your School (${count} schooled).${bonus}`)
  })
  on(Events.WildGoonSlain, () => {
    const bounty = WildGoonConfig.bounty ?? 0
    season.recordGoonSlain(bounty)
    pushLog(bounty > 0
      ? `You slew a wild goon. Bounty: +${bounty}m.`
      : 'You slew a wild goon. The kingdom thanks you.')
  })
  on(Events.KingdomChange, () => clearWildGoons())

  // Coin purchases. Stripe sends the player back with ?coins=success&session_id=…; the server checks with Stripe that it is
  // REALLY paid and records it in its ledger (the webhook records the same payment, so a player who pays and closes the tab
  // still gets their coins next time). The server keeps a running TOTAL of coins paid for; the game keeps (inside the saved
  // game itself, next to the treasury) how much of that total it has already added — profile.coinsApplied — and adds only
  // the difference. Because the treasury and that marker are saved together, coins can't be lost or added twice: not by a
  // crash, a second tab, a slow network or an old save being restored (the missing marker simply makes the game re-add them).
  let settling = false
  async function settleCoinPurchases() {
    if (settling || !account.getAccountInfo().loggedIn) return
    settling = true
    try {
      const params = new URLSearchParams(window.location.search)
      const returning = params.get('coins')
      const sessionId = params.get('session_id')
      if (returning) window.history.replaceState({}, '', window.location.pathname) // a refresh must not redo this
      if (returning === 'cancel') pushLog('Checkout cancelled — you were not charged.')
      if (returning === 'success' && sessionId) {
        const c = await account.confirmCoins(sessionId)
        if (!c.ok) pushLog(c.notPaid
          ? 'Your payment has not completed yet. If you were charged, your coins will arrive automatically within a few minutes.'
          : 'Could not confirm your purchase yet. If you were charged, your coins will arrive automatically — no need to pay again.')
      }
      const { ok, total } = await account.fetchCoinsTotal()
      if (!ok) return
      const applied = Number(getProfile().coinsApplied ?? 0)
      if (total > applied) {
        // First the celebration (game dimmed behind it). The coins are added only when it closes — then the treasury counts up.
        // If the page is closed meanwhile nothing is lost: the ledger still holds them and they are added at the next visit.
        try { await showPurchaseCelebration({ coins: total - applied }) } catch (e) { console.warn('[purchase] celebration failed:', e) }
        const profile = getProfile() // (re-read: the game object can be replaced while the card was open)
        const nowApplied = Number(profile.coinsApplied ?? 0)
        if (total > nowApplied) {
          const delta = total - nowApplied
          profile.coinsApplied = total // set first: earnWealth saves, so the marker and the coins are saved together
          season.earnWealth(delta, { tally: true })
          pushLog(`💰 Purchase complete — ${formatCoins(delta)} added to your treasury!`)
          try { await account.manualPush() } catch { /* the sync ticker retries */ }
        }
      }
    } finally {
      settling = false
    }
  }
  if (import.meta.env?.DEV) window.__invites = { refresh: refreshInvites, attention: inviteAttention, state: getInvites, setPollMode: setInvitePollMode, pollMode: getInvitePollMode } // dev/test hook
  if (import.meta.env?.DEV) window.__music = { elements: music.getElements, state: music.getMusic, warp: music.warp, choose: music.chooseMode, setAuto: music.setAuto } // dev/test hook
  if (import.meta.env?.DEV) window.__supabase = supabaseClient // dev/test hook (lets the browser tests drive the real login() code)
  if (import.meta.env?.DEV) window.__tileToWorld = tileToWorld // dev/test: a tile key's world position
  if (import.meta.env?.DEV) window.__game = { getProfile, getState: () => gameState, persist: persistState, season, profile, account, settle: settleCoinPurchases, celebrate: showPurchaseCelebration, clickValue: season.getClickValue } // dev/test hooks
  on(Events.AccountReady, settleCoinPurchases)
  if (account.getAccountInfo().loggedIn) settleCoinPurchases()
  // Also look again now and then, and whenever the player comes back to this tab, so a payment made elsewhere (or in
  // another tab) shows up without a reload.
  setInterval(settleCoinPurchases, 60_000)
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') settleCoinPurchases() })

  // After a reload caused by "your game was updated in another window", say so (once).
  { const notice = account.takeSyncNotice(); if (notice) setTimeout(() => pushLog(`ℹ ${notice}`), 1500) }

  // Click feedback that doesn't belong to the HUD: a quick tone.
  // Skip on goon-driven clicks to avoid an audio storm.
  let humanClickPending = false
  on('paw:click', () => {
    humanClickPending = true
  })
  on(Events.Click, () => {
    if (humanClickPending) {
      playClick()
      humanClickPending = false
    }
  })

  // Subscribers wire up once (.init).
  goons.init()
  buildings.init()
  feed.init()
  hospital.init() // starving citizens are admitted instead of dying; patients left 48 h are lost (time away counts)
  rest.init() // before tax.init(): a rest that ended while the game was closed is cleared first, so offline wages count
  tax.init()
  population.init()
  events.init()
  raidDirector.init() // scheduled / event-driven / wealth-scaled raids, always announced first
  narrative.init()
  profile.init()
  membership.init()
  marriage.init()
  // toast must subscribe BEFORE achievements.init() runs checkAll, otherwise
  // the legacy-unlock pass on first migration would fire AchievementUnlocked
  // events with no listener.
  achievementToast.init()
  achievements.init()
  adventures.init()
  damage.init()
  election.init()
  idle.init()

  // Account + auto-sync. Runs async; never blocks game init. If the backend
  // is unreachable the rest of the game keeps working in offline mode.
  account.init().catch((err) => console.warn('[boot] account init error:', err))

  // ── Landing page logic ──────────────────────────────────────────────────
  // Unonboarded players land on HOME so the marketing/adopt CTA appears.
  // Returning players land on PLAY.
  siteRouter.setActivePage(profile.isOnboarded() ? 'play' : 'home')

  // On boot AND after defection, re-broadcast active kingdom's state so the
  // scene + UI repaint for the (possibly new) kingdom slice.
  const rebroadcast = () => {
    season.init()
    buildings.broadcast()
    goons.broadcast()
  }
  on(Events.KingdomChange, rebroadcast)
  rebroadcast()

  // Reveal the game only when BOTH the scene is up AND the splash has finished
  // its full 4-line narrative (window.__splashReady, set by index.html). Either
  // side can finish last — whoever does triggers the shared hide.
  requestAnimationFrame(() => {
    window.__appReady = true
    window.__maybeHideSplash = () => {
      if (!window.__appReady) return // reveal as soon as the scene is up
      const splash = document.getElementById('splash')
      if (splash) { splash.classList.add('done'); setTimeout(() => splash.remove(), 700) }
    }
    window.__maybeHideSplash()
  })

  // Render only while the player is actually viewing the game. Other pages
  // skip the WebGL frame to save CPU/GPU; auto-sync + state ticks keep running
  // in the background so the world advances regardless of which page is up.
  let lastFrameMs = performance.now()
  renderer.setAnimationLoop(() => {
    dayNight.tick() // advance sky + lights from local time (cheap; runs on all pages)
    if (siteRouter.getActivePage() !== 'play') return
    const now = performance.now()
    const dt = Math.min(0.1, (now - lastFrameMs) / 1000) // clamp to avoid jumps on tab-switch
    lastFrameMs = now
    season.tickSeason() // CR-2: time-boxed PREP→PLANT→MAINTAIN→HARVEST + harvest payout
    tickHero(dt) // WASD / arrow key movement
    rice.tick(dt) // rows ease toward the height their clicks have earned; tints, hot rows
    crow.tick(dt) // the crow: sweep, peck, eat or leave
    fx.tick(dt)   // the motes
    setFireflyFocus(hero.position.x, hero.position.z, !hasWalkRules() && !adventures.isAdventuring()) // the fireflies stay near the player (and not in the Capital / away)
    hero.userData.tick?.(dt) // advance the rigged hero's Idle/Run/Jump animation
    tickCamera(dt) // smooth camera follow on the hero
    tickSyntyChars(dt) // Synty villagers / raiders: walk vs idle animation
    tickWives()
    tickPeasants() // collision-avoidance: peasants steer around the hero + each other
    tickWildGoons() // hero attack + Temple conversion + lifespan check for wild goons
    tickAdventureScene() // while away: keep stray home objects hidden in the biome
    setSyntyNight(dayNight.night())
    tickLandScene(dt, dayNight.night()) // in a land: keep home hidden, run the land's own tick
    tickCapitalScene(dayNight.night(), dt) // in the Capital: keep home hidden, night glow, wandering townsfolk
    // At night, render through the bloom composer (glow bleed); by day render
    // straight so the transparent canvas keeps showing the CSS sky + stars.
    const nf = dayNight.night()
    if (nf > 0.02) {
      post.setStrength(nf * 0.12)
      post.render()
    } else {
      renderer.render(scene, camera)
    }
  })
}

boot()
